import { HttpClient, HttpParams } from '@angular/common/http'
import { effect, inject } from '@angular/core'
import { BehaviorSubject, catchError, finalize, map, Observable, of, Subject, takeUntil, tap } from 'rxjs'
import { GlobalErrorService } from '@services/global-error.service'
import { PaginatedSidebarResponse } from '../models/sidebar-item.model'
import { SidebarFilter } from '../models/sidebar-filter.model'
import { CategoryService } from '@app/core/auth/services/category.service'

export abstract class AbstractContentSidebarStateService<T> {
  protected readonly http = inject(HttpClient)
  protected readonly globalErrorService = inject(GlobalErrorService)
  protected readonly categoryService = inject(CategoryService)

  protected abstract readonly apiUrl: string
  protected abstract readonly subsystemName: string
  protected abstract readonly errorDescription: string
  protected readonly listEndpoint: string = 'list'
  protected readonly useCategory: boolean = false

  private readonly itemList$$ = new BehaviorSubject<(T | undefined)[]>([])
  public readonly itemList$ = this.itemList$$.asObservable()

  private readonly loading$$ = new BehaviorSubject<boolean>(false)
  public readonly loading$ = this.loading$$.asObservable()

  private readonly filter$$ = new BehaviorSubject<SidebarFilter>({})

  private readonly cancelAll$$ = new Subject<void>()
  private activeRequests = 0

  private allDataLoaded = false
  private readonly loadedPages = new Set<number>()

  protected constructor() {
    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      if (category !== null) {
        this.reload().subscribe()
      }
    })
  }

  public get currentFilter(): SidebarFilter {
    return this.filter$$.value
  }

  public setFilter(filter: SidebarFilter): void {
    this.filter$$.next(filter)
    this.reload().subscribe()
  }

  public ensureLoaded(size = 20): Observable<T[]> {
    if (this.itemList$$.value.length > 0) {
      return of([])
    }
    return this.reload(size)
  }

  public loadMoreItems(page: number, size = 20, force = false): Observable<T[]> {
    if (!force && (this.loading$$.value || this.allDataLoaded || this.loadedPages.has(page))) {
      return of([])
    }

    if (force) {
      this.cancelAll$$.next()
    }

    this.activeRequests++
    this.loading$$.next(true)

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())

    if (this.useCategory) {
      const category = this.categoryService.activeCertificateCategory()
      if (category !== null) {
        params = params.set('category', category)
      }
    }

    const currentFilter = this.filter$$.value
    Object.keys(currentFilter).forEach(key => {
      const value = currentFilter[key]
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params = params.set(key, value.join(','))
          }
        } else {
          params = params.set(key, value.toString())
        }
      }
    })

    return this.http.get<PaginatedSidebarResponse<T> | T[]>(`${this.apiUrl}/${this.listEndpoint}`, { params }).pipe(
      takeUntil(this.cancelAll$$),
      tap(response => {
        const isPaginated = !Array.isArray(response) && response !== null && 'list' in response
        const list = isPaginated ? response.list : response
        const responsePage = isPaginated ? response.page : 0
        const responsePages = isPaginated ? response.pages : 1
        const responseElements = isPaginated ? response.elements : list.length
        const responseSize = isPaginated ? response.size : list.length

        this.loadedPages.add(responsePage)

        if (responsePage >= responsePages - 1) {
          this.allDataLoaded = true
        }

        const currentItems = [...this.itemList$$.value]

        if (responseElements !== undefined && currentItems.length !== responseElements) {
          currentItems.length = responseElements
          currentItems.fill(undefined, this.itemList$$.value.length)
        }

        const startIndex = responsePage * responseSize
        list.forEach((item, index) => {
          currentItems[startIndex + index] = item
        })

        this.itemList$$.next(currentItems)
      }),
      map(response => Array.isArray(response) ? response : response.list),
      catchError((error) => {
        console.error(error)
        this.globalErrorService.reportError({
          subsystemName: this.subsystemName,
          description: this.errorDescription
        })
        return of([])
      }),
      finalize(() => {
        this.activeRequests--
        if (this.activeRequests <= 0) {
          this.activeRequests = 0
          this.loading$$.next(false)
        }
      })
    )
  }

  public reload(size = 20): Observable<T[]> {
    this.itemList$$.next([])
    this.loadedPages.clear()
    this.allDataLoaded = false
    return this.loadMoreItems(0, size, true)
  }
}
